import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api, useLoad, type SeriesQuery } from "../api";
import type { EntryLine, GroupCode, Num, SeriesResponse, Signal, Timeframe, TrendMode } from "../types";
import { ChartStack, type Level, type PaneDef, type Segment, type Span, type VMark } from "../chart/ChartStack";
import { PositionRail } from "../components/PositionRail";
import { paneHelp } from "../lib/paneHelp";
import { CATEGORY_LABEL, fmt, zoneOf } from "../lib/format";
import { linkHandler } from "../lib/router";
import { Score, TrendMark } from "./ScannerPage";
import { Hint, Labeled } from "../components/Hint";
import { HINTS } from "../lib/hints";
import { OiDriverCard, PositionsCard, SeasonalCard } from "../components/MarketExtras";
import { arrangePanes, savePaneLayout, useParams, usePaneLayout, useShowFiltered, type PaneLayout } from "../lib/settings";
import { FilteredSignalsToggle } from "../components/ui";

type Win = "book" | 26 | 52 | 156;

const RANGES: { label: string; weeks: number | null }[] = [
  { label: "1 год", weeks: 52 },
  { label: "3 года", weeks: 156 },
  { label: "5 лет", weeks: 260 },
  { label: "Вся история", weeks: null },
];

export const MODULE_LABEL: Record<string, string> = {
  M0: "Сезонная норма",
  M2: "Экстремум позиции",
  M3: "COT Index",
  M6: "Канал",
  M8: "Открытый интерес",
  M10: "Доля в OI",
  M12: "WILLCO",
  M13: "ProGo",
  M14: "Линия тренда",
  M16: "Позиция",
  M17: "Простой сетап",
  M19: "Полный сетап",
  Q: "Данные",
};

export const KIND_LABEL: Record<string, string> = { setup: "сетап", entry: "вход", event: "событие" };

export function MarketPage({ code, demo }: { code: string; demo: boolean }) {
  const [tf, setTf] = useState<Timeframe>("W");
  const [smart, setSmart] = useState<GroupCode>("YUR");
  // "book" — окна как у Уильямса (COT и WILLCO 26 н., интерес 52 н.); число — одно окно для всех трёх.
  const [win, setWin] = useState<Win>("book");
  const cotShort = win === "book" ? 26 : win;
  const willco = win === "book" ? 26 : win;
  const oi = win === "book" ? 52 : win;
  const [range, setRange] = useState<number | null>(156);
  // Тренд единого сетапа M19: по ТЗ — вариант B (39 н., гл. 15). Сетап «за одну минуту» всегда по 52 н. (гл. 13).
  const [trend, setTrend] = useState<TrendMode>("B");
  // По умолчанию позиции не подменяются (ТЗ: сырые данные не изменяются); сглаживание — опция.
  const [smoothExpiry, setSmoothExpiry] = useState(false);

  // Набор параметров из «Настроек»; переключатель «Окно индикаторов» на странице берёт верх над ним.
  const params = useParams();
  const cs = win === "book" ? params.cotShort : cotShort;
  const wl = win === "book" ? params.willco : willco;
  const oiw = win === "book" ? params.oi : oi;
  const query: SeriesQuery = { code, tf, smart, cotShort: cs, cotLong: params.cotLong, willco: wl, oi: oiw, trend, smoothExpiry, params };
  const { data, error, loading } = useLoad(
    (s) => (demo ? api.demoSeries(query, s) : api.series(query, s)),
    [code, tf, smart, win, trend, smoothExpiry, demo, JSON.stringify(params)],
  );

  const loginTo = `/login?next=${encodeURIComponent(`/ru/${code}`)}`;

  return (
    <main className="page market">
      <a className="back" href="/ru" onClick={linkHandler("/ru")}>
        ← Все рынки
      </a>
      {demo && (
        <div className="demo-banner">
          <span>
            <b>Демо-версия.</b> Здесь открыт этот инструмент целиком. Остальные рынки и сканер с сетапами — после входа.
          </span>
          <a className="btn btn-primary" href={loginTo} onClick={linkHandler(loginTo)}>
            Войти
          </a>
        </div>
      )}
      {error && <div className="notice notice-error">Не удалось загрузить {code}: {error}</div>}
      {!data && loading && <div className="skeleton" aria-busy="true">Считаем индикаторы…</div>}
      {data && (
        <MarketView
          data={data}
          loading={loading}
          controls={
            <Controls
              tf={tf}
              setTf={setTf}
              smart={smart}
              setSmart={setSmart}
              win={win}
              setWin={setWin}
              range={range}
              setRange={setRange}
              trend={trend}
              setTrend={setTrend}
              smoothExpiry={smoothExpiry}
              setSmoothExpiry={setSmoothExpiry}
            />
          }
          rangeBars={range == null ? undefined : tf === "W" ? range : Math.round(range * (data.quality.barsPerWeek || 5))}
        />
      )}
    </main>
  );
}

function MarketView({
  data,
  loading,
  controls,
  rangeBars,
}: {
  data: SeriesResponse;
  loading: boolean;
  controls: ReactNode;
  rangeBars?: number;
}) {
  const s = data.summary;
  const smartCode = data.params.smartGroup;
  const smartName = smartCode === "YUR" ? "Юрлица" : "Физлица";
  const publicName = smartCode === "YUR" ? "Физлица" : "Юрлица";
  const smartGen = smartCode === "YUR" ? "юрлиц" : "физлиц";
  const smartPl = smartCode === "YUR" ? "юрлица" : "физлица";
  const publicPl = smartCode === "YUR" ? "физлица" : "юрлица";
  const cot50 = s.smartCotShort == null ? null : s.smartCotShort > 50;
  const allPanes = useMemo(() => buildPanes(data, smartName, publicName), [data, smartName, publicName]);
  const layout = usePaneLayout();
  const arranged = useMemo(() => arrangePanes(allPanes, layout), [allPanes, layout]);
  // Отфильтрованные сигналы (против тренда или фильтра COT) по умолчанию скрыты: методика их не берёт, а график они
  // загромождают. Настройка общая для графика, списка сигналов и журнала; влияет только на отображение.
  const [showMuted] = useShowFiltered();
  const mutedCount = useMemo(
    () => arranged.filter((x) => x.shown).reduce((n, x) => n + (x.pane.markers?.filter((m) => m.muted).length ?? 0), 0),
    [arranged],
  );
  const panes = useMemo(
    () =>
      arranged
        .filter((x) => x.shown)
        .map((x) => (showMuted || !x.pane.markers ? x.pane : { ...x.pane, markers: x.pane.markers.filter((m) => !m.muted) })),
    [arranged, showMuted],
  );
  const [kindFilter, setKindFilter] = useState<"all" | "setup" | "entry" | "event">("all");
  const kindSignals = useMemo(
    () => [...data.signals].reverse().filter((x) => kindFilter === "all" || x.kind === kindFilter),
    [data, kindFilter],
  );
  const filteredInList = kindSignals.filter((x) => x.filteredByTrend).length;
  const signals = useMemo(
    () => kindSignals.filter((x) => showMuted || !x.filteredByTrend).slice(0, 60),
    [kindSignals, showMuted],
  );
  const vmarks = useMemo(() => buildVmarks(data), [data]);
  // Бары до начала анализа (2023) — только разгон индикаторов: график начинается с 2024 года.
  const analysisStart = useMemo(() => {
    const from = data.quality.analysisFrom;
    if (!from) return 0;
    const i = data.dates.findIndex((d) => d >= from);
    return i < 0 ? 0 : i;
  }, [data]);
  const hasData = data.dates.length > 0;
  const period = data.timeframe === "W" ? "неделю" : "день";

  return (
    <>
      <header className="market-head">
        <div className="title">
          <div className="title-row">
            <h1>{data.market.name}</h1>
            <span className="ticker ticker-lg">{data.market.code}</span>
            {data.market.perpetual && <span className="badge">вечный фьючерс</span>}
          </div>
          <p className="sub">
            <span>{CATEGORY_LABEL[data.market.category] ?? data.market.category}</span>
            {s.lastDate && (
              <span>
                <Labeled hint={HINTS.positions}>Позиции на {fmt.date(s.lastDate)}</Labeled>
              </span>
            )}
            {data.nextExpiry && (
              <span>
                <Labeled hint={HINTS.expiry}>
                  Экспирация {data.nextExpiry.secid}: {fmt.date(data.nextExpiry.date)}
                </Labeled>
              </span>
            )}
          </p>
        </div>
        {s.close != null && (
          <div className="quote">
            <span className="quote-label">
              <Labeled hint={HINTS.price} align="end">
                Закрытие {fmt.date(s.priceDate)}
                {s.priceContract ? `, ${s.priceContract}` : ""}
              </Labeled>
            </span>
            <b>{fmt.price(s.close)}</b>
            {s.closeChangePct != null ? (
              <span className="quote-change">
                <span className={`chg ${s.closeChangePct < 0 ? "down" : "up"}`}>
                  {s.closeChangePct < 0 ? "▼" : "▲"} {fmt.signedPct(s.closeChangePct)}
                </span>{" "}
                к закрытию {fmt.date(s.prevPriceDate)}
              </span>
            ) : (
              <span className="muted">нет цены за {data.timeframe === "W" ? "прошлую неделю" : "прошлый день"}</span>
            )}
            <span className="quote-delay">задержка 1 торговый день</span>
          </div>
        )}
      </header>

      {!hasData && (
        <div className="notice">
          По этому инструменту ещё нет истории. Загрузите её: <code>POST /api/admin/ru/backfill?code={data.market.code}</code>
        </div>
      )}

      {hasData && (
        <section className="overview">
          <div className="overview-rail">
            <div className="kpi">
              <span className="kpi-label">
                <Labeled hint={HINTS.rail}>
                  Индекс COT {smartGen} · {data.params.cotShortWeeks} н.
                </Labeled>
              </span>
              <span className={`kpi-value is-num zone-${zoneOf(s.smartCotShort)}`}>
                {fmt.idx(s.smartCotShort)}
                <small> / 100</small>
              </span>
              <span className="kpi-note">{railVerdict(s.smartCotShort, smartName)}</span>
            </div>
            <PositionRail
              size="lg"
              smart={s.smartCotShort}
              crowd={s.publicCotShort}
              smartLabel={`${smartName} ${fmt.idx(s.smartCotShort)}`}
              crowdLabel={`${publicName} ${fmt.idx(s.publicCotShort)}`}
            />
          </div>
          <dl className="facts">
            <div className={`fact fact-wide fact-primary${s.score > 0 ? " fact-accent fact-bull" : s.score < 0 ? " fact-accent fact-bear" : ""}`}>
              <dt>
                <Labeled hint={HINTS.setup} align="end">
                  Тренд и сетап
                </Labeled>
              </dt>
              <dd>
                <TrendMark t={s.trendUnified} /> <Score score={s.score} setup={s.setup} summary={s} />
              </dd>
              <dd className="note">
                балл покупки {s.scoreBuy} из 5, продажи {s.scoreSell} из 5
              </dd>
              <dd className="note">
                {data.params.trendMode === "B"
                  ? `сетап по ${data.params.smaBWeeks}-недельной средней (гл. 15)`
                  : `сетап по ${data.params.smaWeeks}-недельной средней (гл. 13)`}
                ; 52 н.: <TrendMark t={s.trend} />
                {data.quality.oiUnreliable && !data.quality.oiInUnified ? "; OI фьючерса на индекс в сетапе не участвует" : ""}
              </dd>
            </div>
            <Fact
              primary
              label="Позиция по линиям тренда"
              hint={HINTS.positions16}
              value={s.position > 0 ? "покупка" : s.position < 0 ? "продажа" : "нет"}
              zone={s.position > 0 ? "bull" : s.position < 0 ? "bear" : undefined}
              accent={s.position > 0 ? "bull" : s.position < 0 ? "bear" : undefined}
              note={s.stop != null ? `стоп ${fmt.price(s.stop)}` : "стоп — экстремум 17 дней до входа (гл. 12)"}
            />
            <Fact
              label={`Нетто ${smartGen}`}
              hint={
                <>
                  {HINTS.net}
                  <br />
                  Сейчас: лонги {fmt.int(s.smartLong)} − шорты {fmt.int(s.smartShort)} = {fmt.int(s.smartNet)}.
                </>
              }
              value={fmt.int(s.smartNet)}
              note={`${fmt.signed(s.smartNetChange)} за ${period}`}
            />
            <Fact
              label={`COT за ${data.params.cotLongWeeks} н.${data.params.cotLongWeeks === 156 ? " (3 года)" : ""}`}
              hint={HINTS.cotLong}
              value={fmt.idx(s.smartCotLong)}
              zone={zoneOf(s.smartCotLong)}
              note={s.smartCotLong == null ? "истории меньше окна" : undefined}
            />
            <Fact label={`WILLCO ${data.params.willcoWeeks} н.`} hint={HINTS.willco} value={fmt.idx(s.smartWillco)} zone={zoneOf(s.smartWillco, 75, 25)} />
            <Fact
              label={`Индекс интереса ${data.params.oiWeeks} н.`}
              hint={HINTS.oi}
              value={fmt.idx(s.oiStoch)}
              note={`всего открыто ${fmt.int(s.openInterest)} контр.`}
            />
            <Fact
              label={`Доля ${smartGen} в лонгах`}
              hint={HINTS.share}
              value={fmt.pct(s.smartShareLong)}
              note={`в шортах ${fmt.pct(s.smartShareShort)}`}
            />
            <Fact
              label="COT > 50 (тест книги)"
              hint={HINTS.cot50}
              value={cot50 == null ? "—" : cot50 ? "да" : "нет"}
              zone={cot50 == null ? undefined : cot50 ? "bull" : "bear"}
              note={`сейчас ${fmt.idx(s.smartCotShort)}; в правила сделок канона не входит`}
            />
          </dl>
        </section>
      )}

      {controls}

      {hasData && data.quality.partialLastBar && (
        <div className="notice notice-partial alert" role="status">
          <svg className="alert-icon" width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 3.5 L22 20.5 H2 Z" />
            <path d="M12 10 V14.5 M12 17.6 V17.7" />
          </svg>
          <div>
          <b>Последний {data.timeframe === "W" ? "недельный бар" : "день"} не закрыт.</b> {data.quality.partialReason}. Его
          значения и сигналы предварительные и могут измениться до закрытия (в журнале — статус «предварительно»).
          </div>
        </div>
      )}

      {hasData && data.entryLine && <EntryLineNote e={data.entryLine} />}

      {hasData && (
        <div className={`chart-card ${loading ? "is-loading" : ""}`}>
          <div className="chart-tools">
            <PanePicker arranged={arranged} custom={layout != null} />
            <FilteredSignalsToggle total={mutedCount} />
          </div>
          <PaneNav panes={panes} />
          <ChartStack
            dates={data.dates}
            panes={panes}
            initialBars={rangeBars}
            vmarks={vmarks}
            minStart={analysisStart}
          />
          <div className="chart-foot">
            <p className="chart-help">
              <kbd>Колесо</kbd> масштаб · <kbd>Перетаскивание</kbd> сдвиг · <kbd>Двойной клик</kbd> сброс
            </p>
            <details className="pane-help chart-legend-help">
              <summary>Обозначения на графике</summary>
              <div className="pane-help-body">
                <ul>
                  <li>
                    <b>Вертикали:</b> тонкие пунктирные — экспирации контрактов, синие — многолетние экстремумы нетто (M2),
                    серые — замечания к данным.
                  </li>
                  <li>
                    <b>Скользящие средние:</b> серые пунктиры почти горизонтально — средние {data.params.smaWeeks} и{" "}
                    {data.params.smaBWeeks} недель. Они показывают направление тренда.
                  </li>
                  <li>
                    <b>Линии тренда для входа:</b> зелёная через максимумы — вход в покупку при пробое вверх; красная через
                    минимумы — вход в продажу при пробое вниз.
                  </li>
                  <li>
                    <b>Текущая линия</b> — жирный пунктир с подписью уровня. Если её нет, причина — в строке над графиком.
                    Бледные линии — пробои, отброшенные фильтрами.
                  </li>
                  <li>
                    <b>Узкие цветные полосы</b> — пробои {data.params.channelWeeks}-недельного канала (M6-A). «Балл N» —
                    Score единого сетапа (M19).
                  </li>
                  <li>
                    <b>Полые маркеры</b> — отфильтрованные сигналы (против тренда или фильтра COT). Они сохраняются, но по
                    методике не берутся; показываются, если включено «Показывать отфильтрованные сигналы».
                  </li>
                </ul>
              </div>
            </details>
          </div>
          {data.quality.history && data.quality.history.length > 0 && <HistoryStatus d={data} />}
          {(data.quality.flatWindow.length > 0 || data.quality.inconsistentDates.length > 0) && (
            <p className="notice-inline">
              {data.quality.flatWindow.length > 0 && `Плоское окно на последнем баре (индекс не определён): ${data.quality.flatWindow.join(", ")}. `}
              {data.quality.inconsistentDates.length > 0 &&
                `Нарушено тождество Σлонгов = Σшортов: ${data.quality.inconsistentDates.length} дн., последний ${fmt.date(data.quality.inconsistentDates.at(-1) ?? null)}.`}
            </p>
          )}
        </div>
      )}

      {hasData && (
        <div className="extras">
          <OiDriverCard d={data} smartGen={smartPl} publicGen={publicPl} />
          <SeasonalCard d={data} />
          <PositionsCard d={data} />
        </div>
      )}

      {hasData && (
        <section className="journal">
          <h2>
            <Labeled hint={HINTS.journal}>Сигналы</Labeled>
          </h2>
          <div className="list-tools">
            <Seg
              label="Тип"
              value={kindFilter}
              onChange={setKindFilter}
              options={[["all", "Все"], ["setup", "Сетапы"], ["entry", "Входы"], ["event", "События"]]}
            />
            <FilteredSignalsToggle total={filteredInList} />
          </div>
          {signals.length === 0 ? (
            <p className="empty-state">
              {kindSignals.length > 0 ? "Все сигналы этого типа отфильтрованы — включите «Показывать отфильтрованные сигналы»." : "За выбранную историю сигналов нет."}
            </p>
          ) : (
            <ol>
              {signals.map((sig, i) => (
                <SignalItem key={`${sig.code}-${sig.index}-${i}`} s={sig} />
              ))}
            </ol>
          )}
          <Method />
        </section>
      )}
    </>
  );
}

/**
 * ТЗ 3.1: панели включаются и переставляются пользователем; набор по умолчанию — P0, P1, P2, P6, P8.
 * Раскрывающаяся панель настроек: крупные строки, флажок видимости, кнопки порядка, сброс к набору по умолчанию.
 */
function PanePicker({ arranged, custom }: { arranged: { pane: PaneDef; shown: boolean }[]; custom: boolean }) {
  const ids = arranged.map((x) => x.pane.id);
  const shown = arranged.filter((x) => x.shown).map((x) => x.pane.id);
  const ref = useRef<HTMLDetailsElement>(null);
  const save = (l: PaneLayout) => savePaneLayout(l);
  const toggle = (id: string) =>
    save({ order: ids, shown: shown.includes(id) ? shown.filter((x) => x !== id) : [...shown, id] });
  const move = (i: number, d: number) => {
    const order = [...ids];
    [order[i], order[i + d]] = [order[i + d], order[i]];
    save({ order, shown });
  };
  // Закрывается кликом вне панели и клавишей Esc (фокус возвращается на заголовок).
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      const el = ref.current;
      if (el?.open && !el.contains(e.target as Node)) el.open = false;
    };
    const onKey = (e: KeyboardEvent) => {
      const el = ref.current;
      if (e.key === "Escape" && el?.open) {
        el.open = false;
        el.querySelector("summary")?.focus();
      }
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);
  return (
    <details className="pane-picker" ref={ref}>
      <summary>
        <svg className="pp-icon" width="18" height="18" viewBox="0 0 20 20" aria-hidden="true">
          <rect x="3" y="3" width="14" height="5" rx="1.5" />
          <rect x="3" y="11" width="14" height="6" rx="1.5" />
        </svg>
        <span className="pp-title">Панели</span>
        <span className="pp-count">
          · {shown.length} из {ids.length}
        </span>
        <span className="pp-state">{custom ? "своя раскладка" : "по умолчанию"}</span>
        <svg className="pp-chevron" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M4 6 L8 10 L12 6" />
        </svg>
      </summary>
      <div className="pp-body">
        <p className="pp-help">Флажок — показать панель на графике. Стрелки меняют порядок.</p>
        <ol className="pp-list">
          {arranged.map(({ pane, shown: on }, i) => (
            <li key={pane.id} className={on ? "is-on" : undefined}>
              <label className="pp-row" title={pane.hint}>
                <input type="checkbox" className="check" checked={on} onChange={() => toggle(pane.id)} />
                <span className="pp-name">{pane.title}</span>
              </label>
              <span className="pp-order">
                <button
                  type="button"
                  className="icon-btn"
                  aria-label={`Выше: ${pane.title}`}
                  title="Выше"
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                    <path d="M4 10 L8 6 L12 10" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  aria-label={`Ниже: ${pane.title}`}
                  title="Ниже"
                  disabled={i === arranged.length - 1}
                  onClick={() => move(i, 1)}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                    <path d="M4 6 L8 10 L12 6" />
                  </svg>
                </button>
              </span>
            </li>
          ))}
        </ol>
        <div className="pp-foot">
          <span className="muted">Раскладка сохраняется в этом браузере.</span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={!custom}
            title={custom ? undefined : "Сейчас и так набор по умолчанию"}
            onClick={() => savePaneLayout(null)}
          >
            Вернуть набор по умолчанию
          </button>
        </div>
      </div>
    </details>
  );
}

function railVerdict(v: number | null, who: string) {
  const z = zoneOf(v);
  if (z === "bull") return `${who} у максимума своей позиции — бычья зона.`;
  if (z === "bear") return `${who} у минимума своей позиции — медвежья зона.`;
  if (z === "mid") return `${who} в середине диапазона — экстремума нет.`;
  return "Недостаточно истории для индекса.";
}

function Fact({
  label,
  value,
  note,
  zone,
  hint,
  accent,
  primary,
}: {
  label: string;
  value: string;
  note?: string;
  zone?: string;
  hint?: ReactNode;
  accent?: "bull" | "bear";
  primary?: boolean;
}) {
  return (
    <div className={`fact${primary ? " fact-primary" : ""}${accent ? ` fact-accent fact-${accent}` : ""}`}>
      <dt>{hint ? <Labeled hint={hint}>{label}</Labeled> : label}</dt>
      <dd className={`${zone ? `zone-${zone}` : ""}${/^[\d\s\u00a0−+\-.,%—]+$/.test(value) ? " is-num" : ""}`.trim() || undefined}>
        {value}
      </dd>
      {note && <dd className="note">{note}</dd>}
    </div>
  );
}

export function SignalItem({ s, prefix }: { s: Signal; prefix?: ReactNode }) {
  return (
    <li className={`signal ${s.direction > 0 ? "up" : s.direction < 0 ? "down" : ""} ${s.filteredByTrend ? "muted" : ""}`}>
      <time dateTime={s.date}>{fmt.date(s.date)}</time>
      <span className="tag">{MODULE_LABEL[s.module] ?? s.module}</span>
      <span className="text">
        {prefix}
        <span className="kind">
          {KIND_LABEL[s.kind] ?? s.kind}
          {s.filteredByTrend ? " · отфильтрован" : ""}
          {s.preliminary ? " · предварительно" : ""}
          {s.strength > 1 ? ` · сила ${s.strength}` : ""}
          {s.availableFrom && s.availableFrom !== s.date ? ` · известен с ${fmt.date(s.availableFrom)}` : ""}
          {!s.availableFrom && (s.availableInDays ?? 0) > 0 ? ` · станет известен через ${s.availableInDays} торг. дн.` : ""}
        </span>
        {s.title}
        {s.detail && <small>{s.detail}</small>}
        <Forward s={s} />
      </span>
    </li>
  );
}

/**
 * Аудит 5: достаточность истории по модулям и правило доступности данных. Модуль без нужной истории не считается
 * (значений и событий нет), а не показывается нулём; окно не по книге помечено как исследовательское.
 */
function HistoryStatus({ d }: { d: SeriesResponse }) {
  const h = d.quality.history ?? [];
  const bad = h.filter((x) => x.status !== "ok").length;
  const unit = (u: string, v: number) => (u === "weeks" ? `${Math.round(v)} нед.` : u === "years" ? `${Math.round(v)} г.` : `${Math.round(v)} дн.`);
  const label = (st: string) => (st === "ok" ? "окно набрано" : st === "insufficient_history" ? "мало истории" : "исследовательское окно");
  return (
    <details className="pane-help">
      <summary>
        Достаточность истории и доступность данных{bad > 0 ? ` · ${bad} ${bad === 1 ? "модуль" : "модуля(ей)"} не на полном окне` : ""}
      </summary>
      <div className="pane-help-body">
        {d.quality.availabilityRule && <p>{d.quality.availabilityRule}.</p>}
        <div className="table-scroll">
          <table className="data-table module-history">
            <thead>
              <tr>
                <th className="text">Модуль</th>
                <th>Нужно</th>
                <th>Есть</th>
                <th className="text">Статус</th>
                <th className="text">Первое значение</th>
                <th className="text">Пояснение</th>
              </tr>
            </thead>
            <tbody>
              {h.map((x) => (
                <tr key={x.module + x.title}>
                  <td className="text">
                    {x.module} · {x.title}
                  </td>
                  <td>{unit(x.unit, x.required)}</td>
                  <td>{unit(x.unit, x.available)}</td>
                  <td className={`text status-${x.status}`}>{label(x.status)}</td>
                  <td className="text">{x.firstValid ? fmt.date(x.firstValid) : "—"}</td>
                  <td className="text note">{x.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </details>
  );
}

/** Журнал (ТЗ 3.3): движение цены через 4 / 13 / 26 недель. */
export function Forward({ s }: { s: Signal }) {
  if (s.fwd4 == null && s.fwd13 == null && s.fwd26 == null) return null;
  const one = (label: string, v: Num) => (
    <span>
      {label}: {v == null ? "—" : <b className={v >= 0 ? "up" : "down"}>{fmt.signedPct(v)}</b>}
    </span>
  );
  return (
    <span className="fwd">
      Цена после сигнала — {one("4 н.", s.fwd4)}, {one("13 н.", s.fwd13)}, {one("26 н.", s.fwd26)}
    </span>
  );
}

/** Вертикали через все панели: экспирации, многолетние экстремумы нетто (ТЗ 3.2), замечания к данным. */
function buildVmarks(d: SeriesResponse): VMark[] {
  const out: VMark[] = d.expirations.map((e) => ({ index: e.index, title: `Экспирация ${e.secid}, ${fmt.date(e.date)}` }));
  for (const s of d.signals) {
    if (s.module === "M2") out.push({ index: s.index, title: `${fmt.date(s.date)}: ${s.title}`, className: "vmark-extreme" });
    if (s.module === "Q") out.push({ index: s.index, title: `! ${fmt.date(s.date)}: ${s.title}`, className: "vmark-quality" });
  }
  const idx = new Map(d.dates.map((x, i) => [x, i]));
  for (const x of d.quality.inconsistentDates) {
    const i = idx.get(x);
    if (i != null) out.push({ index: i, title: `! ${fmt.date(x)}: сумма лонгов групп ≠ сумме шортов (data_inconsistent)`, className: "vmark-quality" });
  }
  return out;
}

function Controls(p: {
  tf: Timeframe;
  setTf: (v: Timeframe) => void;
  smart: GroupCode;
  setSmart: (v: GroupCode) => void;
  win: Win;
  setWin: (v: Win) => void;
  range: number | null;
  setRange: (v: number | null) => void;
  trend: TrendMode;
  setTrend: (v: TrendMode) => void;
  smoothExpiry: boolean;
  setSmoothExpiry: (v: boolean) => void;
}) {
  return (
    <div className="controls" role="toolbar" aria-label="Параметры графика">
      <fieldset className="ctrl-group">
        <legend>Данные</legend>
        <Seg label="Бары" value={p.tf} onChange={p.setTf} options={[["W", "Недели"], ["D", "Дни"]]} />
        <Seg
          label="Умные деньги"
          value={p.smart}
          onChange={p.setSmart}
          options={[["YUR", "Юрлица"], ["FIZ", "Физлица"]]}
        />
        <Seg
          label="Экспирации"
          hint={HINTS.expiry}
          value={p.smoothExpiry ? "smooth" : "raw"}
          onChange={(v) => p.setSmoothExpiry(v === "smooth")}
          options={[["smooth", "Сглаживать"], ["raw", "Как есть"]]}
        />
      </fieldset>
      <fieldset className="ctrl-group">
        <legend>Расчёт</legend>
        <Seg
          label="Окно индикаторов"
          hint={
            <>
              <b>По книге</b>: COT и WILLCO за 26 недель, индекс интереса за 52 недели — так у Уильямса (интерес он смотрит
              «12-месячным стохастиком»). <b>26 / 52 / 156 н.</b> — одно окно сразу для COT, WILLCO и индекса интереса.
              Не меняются: COT за год (по нему ищется сетап входа), средняя тренда (52 н.) и сканер.
            </>
          }
          value={String(p.win)}
          onChange={(v) => p.setWin(v === "book" ? "book" : (Number(v) as 26 | 52 | 156))}
          options={[["book", "По книге"], ["26", "26 н."], ["52", "52 н."], ["156", "156 н."]]}
        />
        <Seg
          label="Тренд сетапа"
          hint={HINTS.trendMode}
          value={p.trend}
          onChange={p.setTrend}
          options={[["A", "52 н."], ["B", "39 н."]]}
        />
      </fieldset>
      <fieldset className="ctrl-group">
        <legend>Вид</legend>
        <Seg
          label="Показать"
          value={String(p.range)}
          onChange={(v) => p.setRange(v === "null" ? null : Number(v))}
          options={RANGES.map((r) => [String(r.weeks), r.label] as [string, string])}
        />
      </fieldset>
    </div>
  );
}

function Seg<T extends string>({
  label,
  value,
  onChange,
  options,
  hint,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: [T, string][];
  hint?: ReactNode;
}) {
  return (
    <div className="seg" role="group" aria-label={label}>
      <span className="seg-label">
        {label}
        {hint && <Hint>{hint}</Hint>}
      </span>
      <div className="segmented">
        {options.map(([v, text]) => (
          <button key={v} type="button" className={v === value ? "active" : undefined} aria-pressed={v === value} onClick={() => onChange(v)}>
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

function Method() {
  return (
    <details className="method">
      <summary>Как читать индикаторы</summary>
      <p>
        Идея Ларри Уильямса: на любом рынке есть «умные деньги» — те, кто производит, закупает или профессионально
        хеджирует товар (в американском отчёте CFTC это <i>коммерсанты</i>). Они покупают на слабости и продают на
        силе, часто раньше разворота. Толпа, наоборот, приходит в рынок к концу движения. На Мосбирже роль умных денег
        по умолчанию играют юрлица, роль толпы — физлица. Порядок анализа у Уильямса: сначала позиции и открытый
        интерес, потом тренд, цена — последней («цена может обманывать»).
      </p>
      <ul>
        <li>
          <b>COT Index</b> = (нетто − минимум за окно) / (максимум − минимум) × 100. Показывает не размер позиции, а её
          место в собственной истории за последние 26 недель. Например, 85 — юрлица держат почти самую большую длинную
          позицию за полгода. Выше 80 — зона, где рынок по методике должен расти, ниже 20 — падать. Важно относительное
          положение, а не знак: юрлица могут быть в нетто-шорте, но при индексе 80 этот шорт — самый маленький за
          полгода. Индекс говорит, <i>что</i> рынок должен сделать, но не <i>когда</i>: это зона для поиска входа, а не
          сам вход. Пунктирная линия — тот же индекс за год: по нему ищется сетап для входа (85 и выше — покупки, 15 и ниже — продажи).
        </li>
        <li>
          <b>WILLCO</b> — тот же индекс, но по нетто-позиции, делённой на весь открытый интерес. Зачем: 1 000
          контрактов при открытом интересе 5 000 — это 20% рынка, а при 20 000 — всего 5%. WILLCO показывает, какую
          долю рынка контролируют умные деньги, и не путает их решения с общим ростом рынка. 75–80 и выше — зона
          покупки, 90 и выше — сильный сетап, 25 и ниже — зона продажи. Как и COT Index, это сетап, а не вход.
        </li>
        <li>
          <b>Индекс открытого интереса</b> — положение общего числа открытых контрактов в диапазоне за 52 недели
          (у Уильямса «12-месячный стохастик»). Открытый интерес — это «пловцы в бассейне». Низкий индекс (20 и ниже)
          значит, что толпа потеряла интерес и в рынке остались в основном профессионалы: почти все хорошие точки
          покупки приходились на такие периоды, хотя не каждый низкий интерес — дно. Высокий (80 и выше) — толпа уже в
          рынке, вершина часто близко. Для фьючерсов на индексы с квартальными экспирациями этот индикатор искажён
          (интерес скачет из-за смены контрактов), поэтому там он в полный сетап не входит.
        </li>
        <li>
          <b>ProGo</b> разделяет движение цены на две части. Публика реагирует на новости вечером и ночью и двигает цену
          от вчерашнего закрытия к сегодняшнему открытию (гэп). Профессионалы работают в течение дня: их вклад — от
          открытия до закрытия, даже если день закрылся ниже вчерашнего. Обе линии — средние за 14 дней. Главное —
          расхождения: цена обновляет максимум, а линия профессионалов нет — профессионалы не поддерживают рост
          (серебро, осень 2004 г.). Цена падает, а линия профессионалов растёт — они покупают слабость (золото, май
          2004 г.). Дивергенции показываются только тогда, когда уже есть сетап по COT или WILLCO.
        </li>
        <li>
          <b>Тренд.</b> «Тренд — основа всех прибылей»: сильнее всего работают покупки умных денег в восходящем тренде
          и продажи в нисходящем. Сайт считает два тренда. <i>По 52-недельной средней</i> (гл. 13): тренд вверх, если
          средняя выше, чем неделю назад, вниз — если ниже. По нему строится простой сетап, и по нему сигналы против
          тренда показываются полыми маркерами: их сохраняют, но по методике не торгуют (пока истории цены меньше года,
          тренд не определён, и сигналы тоже полые). <i>По 39-недельной средней</i> (гл. 15) — для полного сетапа: если
          средняя падает, покупать нельзя; если растёт — можно; если средняя плоская, а цены снижаются, — тоже можно;
          недельное закрытие выше средней означает восходящий тренд. Для продаж — зеркально. При плоской средней
          разрешены обе стороны (серый цвет на полосе тренда). «Плоская» — изменение средней за неделю меньше 0,1 %
          реальной цены (не уровня склеенного ряда).
        </li>
        <li>
          <b>Простой сетап</b> («трейдер за одну минуту», гл. 13): тренд по 52-недельной средней вверх и COT Index юрлиц
          80 и выше — сетап на покупку; тренд вниз и COT 20 и ниже — на продажу. В тесте Уильямса такие покупки на
          облигациях были прибыльными в 69% случаев. Сетапы редкие: на некоторых рынках — несколько за годы.
        </li>
        <li>
          <b>Полный сетап и балл</b> (гл. 13, 15). Ядро — три условия сразу: тренд по 39-недельной средней, юрлица в
          зоне (WILLCO от 75 или COT от 80 для покупки, WILLCO до 25 или COT до 20 для продажи) и открытый интерес в
          нужной зоне (низкий для покупки, высокий для продажи). Балл из 5: ядро даёт 3, ещё по 1 — сезонность в ту же
          сторону и физлица на другой стороне по числу счетов. Балл покупки и балл продажи считаются отдельно (ТЗ M19),
          показывается больший; без ядра балл — только подтверждения (0–2). Балл стоит у стрелки сетапа на цене. В ТЗ
          балл до 6: шестой пункт — золото к индексу доллара (M18), на Мосбирже не считается.
        </li>
        <li>
          <b>Линия тренда</b> (гл. 10) — способ войти, когда сетап уже есть. Для покупки на падающем рынке проводят
          линию через два последних краткосрочных максимума (бар, чей максимум выше соседних слева и справа); пробой
          линии вверх — вход. Для продажи — линия через два последних минимума растущего рынка, пробой вниз. Цена входа:
          если бар открылся уже за линией — по открытию (у Уильямса: «если пропустили — продавать на открытии следующего
          дня, не ждать отката»), иначе — по самой линии. Пробой засчитывается (канон Уильямса), если на последней
          опубликованной к открытию дня неделе COT Index за 26 недель или WILLCO в крайней зоне и тренд SMA52 в ту же
          сторону. Окна «8 недель» и правила «один вход на сетап» в книге нет, поэтому в каноне их нет. На графике цены пробитые линии сплошные, текущая — пунктир.
        </li>
        <li>
          <b>Фильтр COT &gt; 50</b> (гл. 10) — для коротких сделок на покупку. Уильямс покупал S&amp;P 500 на открытии
          каждого дня с 1982 г. Когда COT Index за полгода был ниже 50, покупки в сумме дали убыток при просадке
          $209 755; когда выше 50 — 371 сделка, прибыль $76 605, просадка $39 145. Сам по себе это не сигнал, а «отличная
          точка старта». В книге это проверка, а не правило входа, поэтому в сделках канона фильтра нет.
        </li>
        <li>
          <b>Кто меняет интерес</b> (гл. 9, карточка под графиком). Важно не то, растёт ли открытый интерес, а за чей
          счёт. Каждая новая длинная позиция открывается против новой короткой, поэтому смотреть надо на нетто групп за
          4 недели. Интерес и цена растут, а юрлица в нетто продают — рост создала толпа, так выглядит вершина. Интерес
          растёт на падении, а юрлица покупают — пришли сильные деньги, так выглядит дно. Интерес падает, а юрлица
          наращивают лонги — новые покупки только от умных денег, бычий признак.
        </li>
        <li>
          <b>Позиция и стопы</b> (гл. 12, карточка под графиком). Вход — пробой линии тренда после сетапа. Начальный стоп
          — минимум (максимум) 17 последних торговых дней до входа без inside-дней (то же правило 17 дней гл. 12). Только в крупном тренде (52-недельная средняя в сторону позиции) стоп заменяется минимумом (максимумом)
          17 последних торговых дней без inside-дней — даже если он дальше денежного («запас для позиции», гл. 12), и
          дальше движется только в сторону позиции. После выбивания — перевход по максимуму 13 дней, если
          тренд снова в силе. Отметки 10 и 15 недель: «редкий рынок идёт больше 15 недель без серьёзной коррекции».
        </li>
        <li>
          <b>Доля в открытом интересе</b> (гл. 10, панель P7). Доля шорта умных денег у верха своей истории — вершина
          близко (сила 1); у крайнего максимума вместе с сезонной вершиной — сильный сетап на продажу (сила 2); у низа в
          сезонное дно — «боятся продавать». Пороги Уильямса 55/65/30 подобраны на иене, поэтому здесь — перцентили
          собственной истории рынка: {"«высокий»"}, {"«крайне высокий»"} и {"«низкий»"} задаются в «Настройках».
        </li>
        <li>
          <b>Канал 26 недель</b> (гл. 6) — ступенчатые линии на цене: фонды-трендовики входят на 26-недельных максимумах.
          <b> Индекс OI</b> (гл. 8): разворот вверх из зоны ≤ 20 и пробой линии тренда на самом индикаторе — сигналы
          тайминга. <b>Метод Джайлера</b> (гл. 1) — отклонение нетто от нормы того же месяца за прошлые годы.
        </li>
        <li>
          <b>Сезонность</b> (гл. 10, 13, карточка под графиком) — среднее изменение цены для каждой недели года по
          всей истории, сложенное в кривую. Участки подъёма — недели, когда цена обычно росла. Для товаров и валют
          берётся изменение в пунктах, для индексов и акций — в процентах. Это подтверждение, а не самостоятельный
          сигнал: у Уильямса продажи коммерсантов плюс сезонная вершина в золоте в конце 2004 г. дали «2 + 2 = 4».
        </li>
        <li>
          <b>Две группы вместо трёх.</b> В американском отчёте три стороны: коммерсанты, крупные спекулянты (фонды) и
          мелкие трейдеры. На Мосбирже только юрлица и физлица, поэтому нетто физлиц всегда равна нетто юрлиц со знаком
          минус, а их индексы COT в сумме дают 100. Индекс физлиц ничего нового не говорит; независимая информация о
          толпе — число счетов физлиц в лонгах и шортах. Юрлица — смесь хеджеров, банков и фондов, это не точная копия
          американских коммерсантов.
        </li>
      </ul>
    </details>
  );
}

/**
 * Периоды, когда COT и индекс интереса за k баров разошлись в разные стороны минимум на minMove пунктов.
 * COT ↓ и интерес ↑ — медвежья картина; COT ↑ и интерес ↓ — бычья.
 */
function divergenceSpans(cot: Num[], oi: Num[], k: number, minMove: number): Span[] {
  const kind: (0 | 1 | -1)[] = cot.map((c, i) => {
    const c0 = cot[i - k];
    const o = oi[i];
    const o0 = oi[i - k];
    if (i < k || c == null || c0 == null || o == null || o0 == null) return 0;
    const dc = c - c0;
    const dO = o - o0;
    if (dc >= minMove && dO <= -minMove) return 1;
    if (dc <= -minMove && dO >= minMove) return -1;
    return 0;
  });
  const out: Span[] = [];
  for (let i = 0; i < kind.length; i++) {
    if (kind[i] === 0) continue;
    let j = i;
    while (j + 1 < kind.length && kind[j + 1] === kind[i]) j++;
    out.push({
      start: i,
      end: j,
      className: kind[i] > 0 ? "span-bull" : "span-bear",
      title: kind[i] > 0 ? "Юрлица покупают, интерес падает" : "Юрлица продают, интерес растёт",
    });
    i = j;
  }
  return out;
}

/** Полосы по ряду состояний: подряд идущие одинаковые ненулевые значения → один прямоугольник. */
function runs(kind: number[], cls: (k: number) => string | null, title: (k: number) => string): Span[] {
  const out: Span[] = [];
  for (let i = 0; i < kind.length; i++) {
    const c = cls(kind[i]);
    if (!c) continue;
    let j = i;
    while (j + 1 < kind.length && kind[j + 1] === kind[i]) j++;
    out.push({ start: i, end: j, className: c, title: title(kind[i]) });
    i = j;
  }
  return out;
}

const DRIVER_TITLE: Record<number, string> = {
  1: "Ситуация 1: рост OI создан покупками толпы — вершина",
  2: "Ситуация 2: сильные деньги покупают на падении — дно",
  3: "Ситуация 3: новые покупки только от умных денег",
  4: "Ситуация 4: толпа вне рынка, умные деньги покупают",
  5: "Ситуация 5: умные деньги наращивают шорты на ралли",
  6: "Ситуация 6: толпа покупает, умные деньги уходят — слом",
};

/**
 * Отрезки дивергенций ProGo (ТЗ 3.2): BearDiv — от предыдущего максимума закрытия в окне M до бара сигнала, BullDiv —
 * от бара t − M. Только для дивергенций, прошедших в журнал (в контексте сетапа), и только в дневном режиме.
 */
function divergenceSegments(d: SeriesResponse, onPrice: boolean): Segment[] {
  if (d.timeframe !== "D") return [];
  const m = d.params.progoDivDays;
  const out: Segment[] = [];
  for (const s of d.signals) {
    if (s.module !== "M13") continue;
    const i = s.index;
    let j = i - m;
    if (s.direction < 0) {
      let best = -Infinity;
      for (let k = Math.max(0, i - m + 1); k < i; k++) {
        const c = d.price.close[k];
        if (c != null && c > best) {
          best = c;
          j = k;
        }
      }
    }
    if (j < 0) continue;
    const a = onPrice ? d.price.close[j] : d.progo.pro[j];
    const b = onPrice ? d.price.close[i] : d.progo.pro[i];
    if (a == null || b == null) continue;
    out.push({ i1: j, v1: a, i2: i, v2: b, className: "tline div", title: s.title });
  }
  return out;
}

/** Был ли пробой линии на баре отброшен фильтрами (сигнал входа M14 с пометкой «отфильтрован»). */
function filteredEntryAt(d: SeriesResponse, index: number, dir: number): boolean {
  return d.signals.some((s) => s.module === "M14" && s.index === index && s.direction === dir && s.filteredByTrend);
}

/** Подсказка маркера: дата, текст; предварительный сигнал незакрытого бара помечен. */
function signalTitle(s: Signal): string {
  return `${fmt.date(s.date)}: ${s.preliminary ? "[предварительно] " : ""}${s.title}`;
}

/** Окно в неделях коротко: 208 → «4 г.», 26 → «26 н.». */
function weeksLabel(w: number): string {
  return w % 52 === 0 ? `${w / 52} г.` : `${w} н.`;
}

function lvl(y: number, className = "level-solid"): Level {
  return { y, className, label: String(y) };
}

function buildPanes(d: SeriesResponse, smartName: string, publicName: string): PaneDef[] {
  const g = d.groups[d.params.smartGroup];
  const pub = d.groups[d.params.smartGroup === "YUR" ? "FIZ" : "YUR"];
  const smartGenL = smartName === "Юрлица" ? "юрлиц" : "физлиц";
  // На цене — только ключевые события; входы в зоны видны на своих панелях.
  const KEY = new Set([
    "NET_EXTREME_HIGH",
    "NET_EXTREME_LOW",
    "WILLCO_STRONG",
    "SETUP_1M_BUY",
    "SETUP_1M_SELL",
    "UNIFIED_BUY",
    "UNIFIED_SELL",
    "TL_BREAK_BUY",
    "TL_BREAK_SELL",
    "SETUP_ENTRY_BUY",
    "SETUP_ENTRY_SELL",
    "MA_CROSS_BUY",
    "MA_CROSS_SELL",
    "CS_OI_HIGH_SEASON",
    "CS_OI_LOW_SEASON",
    "POS_REENTRY_BUY",
    "POS_REENTRY_SELL",
    "POS_STOP",
  ]);
  const markers = d.signals
    .filter((s) => KEY.has(s.code))
    .map((s) => ({ index: s.index, direction: s.direction, title: signalTitle(s), muted: s.filteredByTrend }));
  // ТЗ 3.2: стрелка единого сетапа — с бейджем балла (Score M19).
  const scoreLabels = d.signals
    .filter((s) => s.module === "M19" && d.price.close[s.index] != null)
    .map((s) => ({ index: s.index, value: d.price.close[s.index] as number, text: `балл ${s.strength}`, className: "point-label score-label" }));
  // Текущая (непробитая) линия входа — подпись с уровнем у её конца: что именно нужно пробить.
  const pendingLineLabels = d.trendLines
    .filter((t) => !t.broken)
    .map((t) => ({
      index: t.i2,
      value: t.p2,
      text: t.dir > 0 ? `покупка выше ${fmt.price(t.p2)}` : `продажа ниже ${fmt.price(t.p2)}`,
      className: `point-label line-label ${t.dir > 0 ? "up" : "down"}`,
    }));
  // ТЗ 3.2: пробой 26-недельного канала (M6-A) — вертикаль на P0.
  const channelSpans: Span[] = d.signals
    .filter((s) => s.module === "M6")
    .map((s) => ({
      start: s.index,
      end: s.index,
      className: s.direction > 0 ? "span-channel-up" : "span-channel-down",
      title: signalTitle(s),
    }));
  const panes: PaneDef[] = [];
  const season = d.seasonal;
  const seasonSpans = season
    ? runs(
        season.window,
        (k) => (k > 0 ? "span-season-top" : k < 0 ? "span-season-bottom" : null),
        (k) => (k > 0 ? "Сезонная вершина рядом (± недели)" : "Сезонное дно рядом (± недели)"),
      )
    : [];

  if (d.price.available) {
    panes.push({
      id: "price",
      title: "P0 Цена",
      hint: "Непрерывный ряд из контрактов с наибольшим открытым интересом, разностная склейка. Ступенчатые линии — канал и стоп",
      height: 300,
      series: [
        {
          kind: "ohlc",
          key: "px",
          label: "Закрытие",
          open: d.price.open,
          high: d.price.high,
          low: d.price.low,
          close: d.price.close,
          up: "var(--bull)",
          down: "var(--bear)",
        },
        { kind: "line", key: "sma", label: `SMA ${d.params.smaWeeks} н.`, color: "var(--ink-3)", values: d.price.sma, width: 1.4, dash: "5 4" },
        { kind: "line", key: "smaB", label: `SMA ${d.params.smaBWeeks} н.`, color: "var(--ink-2)", values: d.price.smaB, width: 1.2, dash: "2 3" },
        { kind: "line", key: "chU", label: `Канал ${d.params.channelWeeks} н.`, color: "var(--smart-soft)", values: d.price.channelUpper, width: 1, step: true },
        { kind: "line", key: "chL", label: "", color: "var(--smart-soft)", values: d.price.channelLower, width: 1, step: true, quiet: true },
        { kind: "line", key: "stop", label: "Стоп M16", color: "var(--bear)", values: d.price.stop, width: 1.4, step: true, dash: "1 2", noCarry: true, emptyText: "нет позиции" },
        { kind: "line", key: "re", label: "Перевход", color: "var(--bull)", values: d.price.reentry, width: 1, step: true, dash: "1 3", quiet: true, noCarry: true },
      ],
      markers,
      labels: [...scoreLabels, ...pendingLineLabels],
      spans: [...seasonSpans, ...channelSpans],
      segments: [
        ...d.trendLines.map((t) => {
          // Пробой, отброшенный фильтром тренда, рисуется бледным: это не вход.
          const filtered = t.broken && filteredEntryAt(d, t.i2, t.dir);
          return {
            i1: t.i1,
            v1: t.p1,
            i2: t.i2,
            v2: t.p2,
            className: `tline ${t.dir > 0 ? "up" : "down"} ${t.broken ? "" : "pending"} ${filtered ? "filtered" : ""}`,
            title: t.broken
              ? `${filtered ? "[отфильтрован — не вход] " : ""}${
                  t.dir > 0
                    ? `Линия тренда пробита вверх — вход в покупку${t.entry != null ? ` по ${fmt.price(t.entry)}` : ""}`
                    : `Линия тренда пробита вниз — вход в продажу${t.entry != null ? ` по ${fmt.price(t.entry)}` : ""}`
                }`
              : t.dir > 0
                ? `Текущая линия входа: покупка при цене выше ${fmt.price(t.p2)}`
                : `Текущая линия входа: продажа при цене ниже ${fmt.price(t.p2)}`,
          };
        }),
        ...divergenceSegments(d, true),
      ],
      format: (v) => fmt.price(v),
    });
    // ТЗ 3.2: «Состояние тренда — полоса состояния под P0».
    panes.push({
      id: "trendbar",
      title: "Тренд",
      hint:
        `Тренд сетапа M19 (${d.params.trendMode === "B" ? `SMA${d.params.smaBWeeks}, гл. 15` : `SMA${d.params.smaWeeks}, гл. 13`}): ` +
        "зелёный — разрешены покупки, красный — продажи, серый — обе стороны (плоская средняя). Тренд-фильтр сигналов — SMA52 в карточке «Тренд и сетап»",
      height: 26,
      fixed: [0, 2],
      series: [],
      spans: [
        ...runs(
          d.trendUnified,
          (k) => (k === 1 ? "span-trend-up" : k === -1 ? "span-trend-down" : k === 2 ? "span-trend-both" : null),
          (k) => (k === 1 ? "M19: покупки" : k === -1 ? "M19: продажи" : "M19: обе стороны"),
        ),
      ],
    });
  }

  const netLabels = d.signals
    .filter((s) => s.module === "M2" && Math.abs(g.recordYears[s.index] ?? 0) >= 4 && g.net[s.index] != null)
    .map((s) => ({
      index: s.index,
      value: g.net[s.index] as number,
      text: `${(g.recordYears[s.index] ?? 0) > 0 ? "макс." : "мин."} за ${Math.abs(g.recordYears[s.index])} л.`,
    }));
  panes.push({
    id: "net",
    title: `P1 Нетто-позиция: ${smartName.toLowerCase()}`,
    hint: `Длинные минус короткие позиции. Пунктир — максимум и минимум за ${d.params.extremeWeeks} недель (M2); точки — валовые лонги на своей шкале`,
    height: 140,
    zero: true,
    series: [
      { kind: "hist", key: "net", label: smartName, values: g.net, pos: "var(--smart)", neg: "var(--smart-soft)" },
      { kind: "line", key: "hh", label: `HH ${weeksLabel(d.params.extremeWeeks)}`, color: "var(--bull)", values: g.netHigh, width: 1, dash: "4 3" },
      { kind: "line", key: "ll", label: `LL ${weeksLabel(d.params.extremeWeeks)}`, color: "var(--bear)", values: g.netLow, width: 1, dash: "4 3" },
      { kind: "line", key: "gl", label: "Лонги (своя шкала)", color: "var(--ink-3)", values: g.longs, width: 1, dash: "1 3", axis: "right" },
    ],
    markers: d.price.available
      ? d.signals.filter((s) => s.module === "M2").map((s) => ({ index: s.index, direction: s.direction, title: signalTitle(s), muted: s.filteredByTrend }))
      : markers,
    labels: netLabels,
    format: (v) => fmt.compact(v),
  });

  panes.push({
    id: "cot",
    title: `P2 COT Index ${smartGenL}`,
    hint: `Положение нетто-позиции в диапазоне окна, 0–100. Сплошная — ${d.params.cotShortWeeks} н., пунктир — ${d.params.cotLongWeeks} н.${d.params.cotLongWeeks === 156 ? " (3 года, гл. 4)" : " (исследовательское окно, по книге — 156)"}. Индекс физлиц — контр-индикатор (= 100 − юрлиц)`,
    height: 150,
    fixed: [0, 100],
    bands: [
      { from: 80, to: 100, className: "band-bull" },
      { from: 0, to: 20, className: "band-bear" },
    ],
    levels: [lvl(80, "level-bull"), lvl(20, "level-bear"), { y: 75, className: "level-thin" }, { y: 25, className: "level-thin" }, { y: 50, className: "level-mid", label: "50" }],
    series: [
      { kind: "line", key: "cs", label: `${smartName}, ${d.params.cotShortWeeks} н.`, color: "var(--smart)", values: g.cotShort, width: 2 },
      { kind: "line", key: "cl", label: `${smartName}, ${d.params.cotLongWeeks} н.`, color: "var(--smart)", values: g.cotLong, width: 1.2, dash: "4 4" },
      { kind: "line", key: "ps", label: `${publicName} (контр-индикатор)`, color: "var(--crowd)", values: pub.cotShort, width: 1.2, dash: "2 3" },
    ],
    markers: d.signals
      .filter((s) => s.module === "M3")
      .map((s) => ({ index: s.index, direction: s.direction, title: signalTitle(s), muted: s.filteredByTrend })),
    format: (v) => fmt.idx(v),
  });

  panes.push({
    id: "willco",
    title: `P8 WILLCO, ${d.params.willcoWeeks} н.`,
    hint: `Стохастик (нетто / открытый интерес) за ${d.params.willcoWeeks} н. Залитая полоса 75–80 — зона срабатывания покупки, 90 — сильный сетап`,
    height: 130,
    fixed: [0, 100],
    bands: [
      { from: 75, to: 80, className: "band-trigger" },
      { from: 80, to: 100, className: "band-bull" },
      { from: 0, to: 25, className: "band-bear" },
    ],
    levels: [lvl(90, "level-strong"), lvl(25, "level-bear"), lvl(20, "level-thin")],
    series: [{ kind: "line", key: "w", label: smartName, color: "var(--smart)", values: g.willco, width: 2 }],
    markers: d.signals
      .filter((s) => s.module === "M12")
      .map((s) => ({ index: s.index, direction: s.direction, title: signalTitle(s), muted: s.filteredByTrend })),
    format: (v) => fmt.idx(v),
  });

  panes.push({
    id: "share",
    title: `P7 Доля ${smartGenL} в открытом интересе`,
    hint:
      `CL/OI и CS/OI, %. Пунктир — ${d.params.csHighPct}-й и ${d.params.csLowPct}-й перцентили доли шорта в собственной истории ` +
      "(пороги 55/65/30 у Уильямса подобраны на иене, ТЗ M10)",
    height: 130,
    series: [
      { kind: "line", key: "sl", label: "В лонгах (CL/OI)", color: "var(--bull)", values: g.shareLong, width: 1.6 },
      { kind: "line", key: "ss", label: "В шортах (CS/OI)", color: "var(--bear)", values: g.shareShort, width: 1.6 },
      { kind: "line", key: "sh", label: `p${d.params.csHighPct}`, color: "var(--bear)", values: g.shortHigh, width: 1, dash: "4 3" },
      { kind: "line", key: "sw", label: `p${d.params.csLowPct}`, color: "var(--bear)", values: g.shortLow, width: 1, dash: "1 3" },
    ],
    markers: d.signals
      .filter((s) => s.module === "M10")
      .map((s) => ({ index: s.index, direction: s.direction, title: signalTitle(s), muted: s.filteredByTrend })),
    spans: seasonSpans,
    format: (v) => `${v.toFixed(0)}%`,
  });

  panes.push({
    id: "oiLine",
    title: "P5 Открытый интерес",
    hint: "Общий OI (лонги юрлиц + лонги физлиц) и нетто умных денег на своей шкале. Цветные полосы — ситуации «кто меняет OI» (гл. 9), подсказка — номер",
    height: 130,
    series: [
      { kind: "line", key: "oiT", label: "OI", color: "var(--ink-2)", values: d.oi.total, width: 1.6 },
      { kind: "line", key: "oiN", label: `Нетто ${smartGenL} (своя шкала)`, color: "var(--smart)", values: g.net, width: 1.2, dash: "4 3", axis: "right" },
    ],
    spans: runs(
      d.oi.driver,
      (k) => (k === 0 ? null : [2, 3, 4].includes(k) ? "span-bull" : "span-bear"),
      (k) => DRIVER_TITLE[k] ?? "",
    ),
    format: (v) => fmt.compact(v),
  });

  panes.push({
    id: "oi",
    title: `P6 Индекс открытого интереса, ${d.params.oiWeeks} н.`,
    hint: "Стохастик OI всего рынка. Линии — тренд на самом индикаторе (гл. 8, рис. 8.11): пробой вверх в нижней зоне — покупка, вниз в верхней — продажа",
    height: 120,
    fixed: [0, 100],
    bands: [
      { from: 0, to: 20, className: "band-calm" },
      { from: 80, to: 100, className: "band-crowd" },
    ],
    levels: [lvl(80), lvl(20)],
    series: [{ kind: "line", key: "oi", label: "Индекс", color: "var(--ink-2)", values: d.oi.stoch, width: 1.8 }],
    segments: d.oi.lines.map((t) => ({
      i1: t.i1,
      v1: t.p1,
      i2: t.i2,
      v2: t.p2,
      className: `tline oi ${t.broken ? "" : "pending"}`,
      title: t.dir > 0 ? "Нисходящая линия по максимумам индекса OI" : "Восходящая линия по минимумам индекса OI",
    })),
    markers: d.signals
      .filter((s) => s.module === "M8" && (s.code.startsWith("OI_TURN") || s.code.startsWith("OI_TL")))
      .map((s) => ({ index: s.index, direction: s.direction, title: `${fmt.date(s.date)}: ${s.title}` })),
    format: (v) => fmt.idx(v),
  });

  // Гл. 9 «Opening Up on Open Interest»: важно не то, растёт ли интерес, а кто его меняет.
  const k = d.oi.changeBars;
  panes.push({
    id: "cotoi",
    title: `COT и открытый интерес`,
    hint:
      "Индекс COT умных денег и индекс открытого интереса на одной шкале. Красная подсветка: за 4 недели COT упал, а интерес вырос — " +
      "рост интереса создаёт толпа, пока умные деньги продают (картина перед вершиной). Зелёная: COT вырос, а интерес упал — " +
      "умные деньги покупают, пока толпа уходит (картина перед ростом)",
    height: 150,
    fixed: [0, 100],
    bands: [
      { from: 80, to: 100, className: "band-quiet" },
      { from: 0, to: 20, className: "band-quiet" },
    ],
    levels: [{ y: 50, className: "level-mid" }],
    spans: divergenceSpans(g.cotShort, d.oi.stoch, k, 15),
    series: [
      { kind: "line", key: "c", label: `COT ${smartGenL}, ${d.params.cotShortWeeks} н.`, color: "var(--smart)", values: g.cotShort, width: 2 },
      { kind: "line", key: "o", label: `Интерес, ${d.params.oiWeeks} н.`, color: "var(--crowd)", values: d.oi.stoch, width: 1.6 },
    ],
    format: (v) => fmt.idx(v),
  });

  const kw = d.timeframe === "W" ? d.oi.changeBars : Math.round(d.oi.changeBars / Math.max(1, d.quality.barsPerWeek));
  panes.push({
    id: "driver",
    title: `Кто меняет интерес: лонги за ${kw} нед.`,
    hint:
      "Столбики — изменение общего открытого интереса за 4 недели. Линии — сколько из него дали длинные позиции каждой группы " +
      "(интерес = лонги юрлиц + лонги физлиц). Рост интереса за счёт физлиц при продажах юрлиц — признак вершины, " +
      "покупки юрлиц при уходе толпы — признак дна (гл. 9)",
    height: 130,
    zero: true,
    series: [
      { kind: "hist", key: "dt", label: "Интерес", values: d.oi.dTotal, pos: "var(--line)", neg: "var(--line)" },
      { kind: "line", key: "ds", label: smartName, color: "var(--smart)", values: d.oi.dSmartLong, width: 1.8 },
      { kind: "line", key: "dp", label: publicName, color: "var(--crowd)", values: d.oi.dPublicLong, width: 1.6 },
    ],
    format: (v) => fmt.compact(v),
  });

  // ТЗ 3.1: P9 ProGo — только дневной режим (дневные данные, гл. 10).
  if (d.progo.available && d.timeframe === "D") {
    panes.push({
      id: "progo",
      title: "P9 ProGo",
      hint: "Профессионалы: среднее (закрытие − открытие) за 14 дней. Публика: среднее (открытие − вчерашнее закрытие). Оранжевые отрезки — дивергенции",
      height: 120,
      zero: true,
      series: [
        { kind: "line", key: "pro", label: "Профессионалы", color: "var(--smart)", values: d.progo.pro, width: 1.8 },
        { kind: "line", key: "pub", label: "Публика", color: "var(--crowd)", values: d.progo.pub, width: 1.4 },
      ],
      segments: divergenceSegments(d, false),
      format: (v) => fmt.price(v),
    });
    panes.push({
      id: "progoAd",
      title: "ProGo: линии A/D",
      hint: "Исходная форма индикатора автора: накопленные суммы (закрытие − открытие) и (открытие − вчерашнее закрытие)",
      height: 110,
      series: [
        { kind: "line", key: "proAd", label: "Профессионалы A/D", color: "var(--smart)", values: d.progo.proAd, width: 1.6 },
        { kind: "line", key: "pubAd", label: "Публика A/D", color: "var(--crowd)", values: d.progo.pubAd, width: 1.2 },
      ],
      format: (v) => fmt.price(v),
    });
  }

  if (season) {
    panes.push({
      id: "season",
      title: "P10 Сезонность",
      hint: `Сезонная кривая недели года, повторённая для каждого года (${season.years} л. истории цен). Заливка — у сезонной вершины / дна`,
      height: 100,
      zero: true,
      series: [{ kind: "line", key: "seas", label: "Seas(w)", color: "var(--ink-2)", values: season.curve, width: 1.4 }],
      spans: seasonSpans,
      format: (v) => (Math.abs(v) >= 100 ? fmt.compact(v) : v.toFixed(Math.abs(v) >= 1 ? 1 : 3)),
    });
  }

  panes.push({
    id: "jiler",
    title: `M0 Отклонение от сезонной нормы (метод Джайлера)`,
    hint:
      `Нетто ${smartGenL} в % от OI минус средняя на конец того же месяца за прошлые годы (до ${d.jiler.years} л.), ` +
      `${d.jiler.mode === "PP" ? "в процентных пунктах" : "относительное, %"}. |Dev| < 5 — игнорировать, > 40 — аномалия (гл. 1)`,
    height: 100,
    zero: true,
    levels: [lvl(5, "level-thin"), lvl(-5, "level-thin"), lvl(40, "level-bear"), lvl(-40, "level-bear")],
    series: [{ kind: "line", key: "jd", label: "Dev", color: "var(--smart)", values: d.jiler.dev, width: 1.6 }],
    format: (v) => v.toFixed(1),
  });

  const crowd = d.groups.FIZ;
  panes.push({
    id: "holders",
    title: "Счета физлиц в позициях",
    hint: "Число счетов физлиц с длинной и короткой позицией — независимая мера участия толпы",
    height: 110,
    series: [
      { kind: "line", key: "hl", label: "В лонгах", color: "var(--bull)", values: crowd.holdersLong, width: 1.4 },
      { kind: "line", key: "hs", label: "В шортах", color: "var(--bear)", values: crowd.holdersShort, width: 1.4 },
    ],
    format: (v) => fmt.compact(v),
  });

  for (const pn of panes) pn.help = paneHelp(pn.id, d, smartName, publicName);
  return panes;
}

/** M14-C: где линия входа или почему её сейчас нет — чтобы не искать на графике то, чего там нет. */
function EntryLineNote({ e }: { e: EntryLine }) {
  if (e.state.startsWith("setup_")) {
    const head =
      e.state === "setup_in"
        ? "Позиция по сетапу открыта"
        : e.state === "setup_wait"
          ? `Вход по сетапу: ${e.dir > 0 ? "покупка" : "продажа"} на открытии после закрытия недели`
          : e.state === "setup_done"
            ? "Вход по этому сетапу уже был"
            : "Сетапа 4+ нет";
    const tone = e.state === "setup_wait" || e.state === "setup_in" ? (e.dir < 0 ? "down" : "up") : "idle";
    return (
      <div className={`entry-note entry-${tone}`} role="status">
        <b>{head}.</b> {e.text}
      </div>
    );
  }
  const head =
    e.state === "line"
      ? e.dir < 0
        ? `Линия входа: продажа ниже ${fmt.price(e.level)}`
        : `Линия входа: покупка выше ${fmt.price(e.level)}`
      : e.state === "window"
        ? "Линии входа нет: вход по этому сетапу уже сделан"
        : e.state === "no_line"
          ? "Линии входа пока нет"
          : "Линии входа нет: нет сетапа";
  const tone = e.state === "line" ? (e.dir < 0 ? "down" : "up") : "idle";
  return (
    <div className={`entry-note entry-${tone}`} role="status">
      <b>{head}.</b> {e.text}
    </div>
  );
}

/**
 * Быстрая навигация по панелям графика: лента капсул прилипает под шапкой, подсвечивает панель в центре экрана,
 * по клику плавно прокручивает к ней.
 */
function PaneNav({ panes }: { panes: PaneDef[] }) {
  const [active, setActive] = useState<string | null>(null);
  const ids = panes.map((p) => p.id).join("|");
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (es) => {
        for (const e of es) if (e.isIntersecting) setActive(e.target.id.replace(/^pane-/, ""));
      },
      { rootMargin: "-35% 0px -60% 0px" },
    );
    for (const id of ids.split("|")) {
      const el = document.getElementById(`pane-${id}`);
      if (el) io.observe(el);
    }
    return () => io.disconnect();
  }, [ids]);
  if (panes.length < 3) return null;
  const go = (id: string) => {
    const el = document.getElementById(`pane-${id}`);
    if (!el) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    // Отступ — под шапкой и самой лентой: панель встаёт сразу под ними.
    const bar = document.querySelector(".topbar")?.getBoundingClientRect().height ?? 0;
    const nav = document.querySelector(".pane-nav")?.getBoundingClientRect().height ?? 0;
    const top = el.getBoundingClientRect().top + window.scrollY - bar - nav - 16;
    window.scrollTo({ top, behavior: reduce ? "auto" : "smooth" });
    setActive(id);
  };
  return (
    <nav className="pane-nav" aria-label="Панели графика">
      {panes.map((p) => {
        const m = p.title.match(/^([PM]\d+)\s+(.*)$/);
        return (
          <button
            key={p.id}
            type="button"
            className={active === p.id ? "active" : undefined}
            onClick={() => go(p.id)}
            title={p.title}
          >
            {m ? (
              <>
                <b>{m[1]}</b> {shortTitle(m[2])}
              </>
            ) : (
              shortTitle(p.title)
            )}
          </button>
        );
      })}
    </nav>
  );
}

function shortTitle(t: string) {
  const map: [string, string][] = [
    ["Нетто", "Нетто"],
    ["Индекс открытого", "Индекс OI"],
    ["Доля", "Доля в OI"],
    ["Отклонение от сезонной", "Джайлер"],
    ["Кто меняет", "Кто меняет OI"],
    ["Счета физлиц", "Счета физлиц"],
    ["COT и открытый", "COT и OI"],
    ["WILLCO", "WILLCO"],
  ];
  for (const [k, v] of map) if (t.startsWith(k)) return v;
  return t.length > 26 ? `${t.slice(0, 25)}…` : t;
}
